import React, {useEffect, useState, useContext} from "react";
import {
    Box, Stack, Typography, IconButton, Button,
    Modal, Fade, InputBase, Alert, CircularProgress, Select, MenuItem
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Alert02Icon, Cancel01Icon, CheckIcon, Plug01Icon, LinkIcon, Key01Icon} from "@hugeicons/core-free-icons";
import {useAtomValue} from "jotai";
import {Context} from "../../../../context/AppContext"
import {flowUidAtom, projectAtom} from "../atoms"
import {SubProjectOption} from "../../SubProjectSelect";

const controlSx = {
  height: 40,
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1.25,
  borderRadius: "10px",
  bgcolor: "#FAFAFD",
  color: "#6B6B80",
  fontSize: 13,
};

const initServer = {
    name: "",
    endpoint: "",
    active: true,
    flow_uid: "",
    auth_token: "",
};

const isValidUrl = (url) => {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
};

export const AddMCPServerModalNew = ({open, onClose, onSuccess}) => {
    const defaultFlowUid = useAtomValue(flowUidAtom);
    const project = useAtomValue(projectAtom);
    const subProjects = project?.subProjects || [];
    const [server, setServer] = useState({...initServer, flow_uid: defaultFlowUid});
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const {testMCPConnection, createMCPServer} = useContext(Context);

    useEffect(() => {
        if (open) {
            setServer({...initServer, flow_uid: defaultFlowUid});
        }
    }, [open, defaultFlowUid]);

    const onChange = (key, value) => {
        setServer(prev => ({...prev, [key]: value}));
        if (key === "endpoint" || key === "auth_token") setTestResult(null);
    };

    const handleClose = () => {
        setServer({...initServer, flow_uid: defaultFlowUid});
        onClose();
        setTestResult(null);
    };

    const handleSave = async () => {
        if (!server.name || !server.endpoint) return;
        const selectedSubProject = subProjects.find(item => item.flowUid === server.flow_uid);
        const payload = {
            name: server.name,
            endpoint: server.endpoint,
            active: server.active,
            flow_uid: server.flow_uid,
            project_id: project?.id != null ? String(project.id) : undefined,
            subproject_id: selectedSubProject?.id != null ? String(selectedSubProject.id) : undefined,
            auth_token: server.auth_token
        };
        try {
            setSaving(true);
            const created = await createMCPServer(payload);
            if (created) {
                onSuccess?.();
                handleClose();
            }
        } catch (e) {
            console.error("Failed to add MCP server:", e);
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        if (!server.endpoint) return;
        setTesting(true);
        setTestResult(null);
        const data = await testMCPConnection({
            endpoint: server.endpoint,
            auth_token: server.auth_token || null
        });
        setTestResult(data);
        setTesting(false);
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Fade in={open}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: {xs: "calc(100% - 24px)", md: "min(520px, 92vw)"},
                    bgcolor: '#fff',
                    borderRadius: '20px',
                    boxShadow: '0 24px 70px rgba(35, 38, 77, 0.22)',
                    p: {xs: 2.5, md: 3},
                    outline: 'none',
                }}>
                    {/* Header */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 3}}>
                        <Box>
                            <Typography sx={{fontSize: 18, fontWeight: 700, color: '#23264D', letterSpacing: '-0.01em'}}>
                                Add MCP Server
                            </Typography>
                            <Typography sx={{fontSize: 13, color: '#8A8AA0', mt: 0.5}}>
                                Configure a new Model Context Protocol server
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={handleClose} sx={{color: '#8A8AA0'}}>
                            <HugeiconsIcon icon={Cancel01Icon} size={18} />
                        </IconButton>
                    </Stack>

                    {/* Form */}
                    <Stack gap={2} sx={{mb: 2.5}}>
                        {/* Name Field */}
                        <Box>
                            <Typography sx={{fontSize: 12, fontWeight: 600, color: '#6B6B80', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                                Server Name
                            </Typography>
                            <Box sx={{...controlSx}}>
                                <InputBase
                                    placeholder="e.g., My MCP Server"
                                    value={server.name}
                                    onChange={e => onChange("name", e.target.value)}
                                    inputProps={{"aria-label": "Server name"}}
                                    autoFocus
                                    sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                                />
                            </Box>
                        </Box>

                        {/* Sub Project Field */}
                        <Box>
                            <Typography sx={{fontSize: 12, fontWeight: 600, color: '#6B6B80', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                                Sub Project
                            </Typography>
                            <Select
                                variant="standard"
                                disableUnderline
                                fullWidth
                                value={server.flow_uid}
                                onChange={e => onChange("flow_uid", e.target.value)}
                                renderValue={(value) => {
                                    const selected = subProjects.find(item => item.flowUid === value);
                                    return selected
                                        ? <SubProjectOption subProject={selected}/>
                                        : <Typography sx={{fontSize: 13, color: "#8A8AA0"}}>Select a sub project</Typography>;
                                }}
                                sx={{
                                    ...controlSx,
                                    "& .MuiSelect-select": {py: 0, pr: "28px !important", display: "flex", alignItems: "center"},
                                    "& .MuiSelect-icon": {color: "#8A8AA0"},
                                }}
                                MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
                            >
                                {subProjects.map(subProject => (
                                    <MenuItem key={subProject.id} value={subProject.flowUid} dense>
                                        <SubProjectOption subProject={subProject}/>
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>

                        {/* Endpoint Field */}
                        <Box>
                            <Typography sx={{fontSize: 12, fontWeight: 600, color: '#6B6B80', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                                Endpoint URL
                            </Typography>
                            <Box sx={{...controlSx, ...(!!server.endpoint && !isValidUrl(server.endpoint) && {borderColor: '#DC2626'})}}>
                                <HugeiconsIcon icon={LinkIcon} size={16} />
                                <InputBase
                                    placeholder="https://..."
                                    value={server.endpoint}
                                    onChange={e => onChange("endpoint", e.target.value)}
                                    inputProps={{"aria-label": "Endpoint URL"}}
                                    sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                                />
                            </Box>
                            {!!server.endpoint && !isValidUrl(server.endpoint) && (
                                <Typography sx={{fontSize: 12, color: '#DC2626', mt: 0.5}}>Invalid URL format</Typography>
                            )}
                        </Box>

                        {/* Auth Token Field */}
                        <Box>
                            <Typography sx={{fontSize: 12, fontWeight: 600, color: '#6B6B80', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                                Auth Token (Optional)
                            </Typography>
                            <Box sx={{...controlSx}}>
                                <HugeiconsIcon icon={Key01Icon} size={16} />
                                <InputBase
                                    type="password"
                                    placeholder="Enter authentication token"
                                    value={server.auth_token}
                                    onChange={e => onChange("auth_token", e.target.value)}
                                    inputProps={{"aria-label": "Auth token"}}
                                    sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                                />
                            </Box>
                        </Box>
                    </Stack>

                    {/* Test Connection */}
                    <Box sx={{mb: 2.5}}>
                        <Button
                            size="small"
                            disabled={testing || !server.endpoint || !isValidUrl(server.endpoint)}
                            startIcon={testing ? <CircularProgress size={14}/> : <HugeiconsIcon icon={Plug01Icon} size={16}/>}
                            onClick={handleTest}
                            sx={{
                                textTransform: "none",
                                borderRadius: '10px',
                                fontWeight: 600,
                                fontSize: 13,
                                color: '#8491F7',
                                '&:hover': {bgcolor: 'rgba(132, 145, 247, 0.08)'},
                            }}
                        >
                            {testing ? "Testing..." : "Test Connection"}
                        </Button>

                        {testResult && (
                            <Alert
                                severity={testResult.ok ? "success" : "error"}
                                icon={testResult.ok ? <HugeiconsIcon icon={CheckIcon} size={18}/> : <HugeiconsIcon icon={Alert02Icon} size={18}/>}
                                sx={{
                                    borderRadius: '10px',
                                    fontSize: 13,
                                    mt: 1.5,
                                    ...(testResult.ok
                                        ? {bgcolor: 'rgba(47, 154, 106, 0.08)', color: '#2F9A6A', border: '1px solid #2F9A6A'}
                                        : {bgcolor: 'rgba(220, 38, 38, 0.08)', color: '#DC2626', border: '1px solid #DC2626'})
                                }}
                            >
                                {testResult.message}
                                {testResult.ok && testResult.server_name && (
                                    <Box component="span" sx={{display: "block", fontSize: 12, opacity: 0.8, mt: 0.5}}>
                                        {testResult.server_name}
                                        {testResult.protocol_version && ` · MCP ${testResult.protocol_version}`}
                                        {testResult.tool_count != null && ` · ${testResult.tool_count} tools`}
                                    </Box>
                                )}
                            </Alert>
                        )}
                    </Box>

                    {/* Actions */}
                    <Stack direction="row" gap={1.5} justifyContent="flex-end">
                        <Button
                            onClick={handleClose}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: 13,
                                borderRadius: '10px',
                                color: '#374151',
                                bgcolor: '#FAFAFD',
                                '&:hover': {bgcolor: '#ECECF3'},
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={!server.name || !server.flow_uid || !server.endpoint || saving || !testResult?.ok || !isValidUrl(server.endpoint)}
                            onClick={handleSave}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: 13,
                                borderRadius: '10px',
                                bgcolor: '#374151',
                                color: '#fff',
                                '&:hover': {bgcolor: '#1F2937'},
                                '&.Mui-disabled': {bgcolor: '#D1D5DB', color: '#9CA3AF'},
                            }}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </Stack>
                </Box>
            </Fade>
        </Modal>
    );
};

export default AddMCPServerModalNew;
